import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { consultasAPI, clientesAPI, authAPI } from '../services/api';
import ConsultaForm from './ConsultaForm';
import Editor from './Editor';
import { getLabels } from '../utils/userLabels';
import { safeHtml } from '../utils/sanitize';
import './Consultas.css';

function Consultas() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tipoUsuario, setTipoUsuario] = useState('coach');
  const [facturacionHabilitada, setFacturacionHabilitada] = useState(false);

  useEffect(() => {
    authAPI.check().then(res => {
      if (res.data.authenticated && res.data.user) {
        setTipoUsuario(res.data.user.tipo_usuario || 'coach');
        setFacturacionHabilitada(res.data.user.facturacion_habilitada === 1 || res.data.user.facturacion_habilitada === true);
      }
    }).catch(() => {});
  }, []);

  const labels = getLabels(tipoUsuario);
  const [consultas, setConsultas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingConsulta, setEditingConsulta] = useState(null);
  const [filtroCliente, setFiltroCliente] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // Estado para edición inline: { consultaId: { fieldName: value } }
  const [editingFields, setEditingFields] = useState({});
  const [savingFields, setSavingFields] = useState({});

  const loadClientes = useCallback(async () => {
    try {
      const response = await clientesAPI.getAll(true);
      setClientes(response.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadConsultas = useCallback(async () => {
    try {
      setLoading(true);
      const clienteId = filtroCliente ? parseInt(filtroCliente) : null;
      const response = await consultasAPI.getAll(clienteId);
      setConsultas(response.data);
      setError(null);
    } catch (err) {
      setError(`Error al cargar las ${labels.sesiones_lower}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filtroCliente, labels.sesiones_lower]);

  useEffect(() => {
    loadClientes();
  }, [loadClientes]);

  useEffect(() => {
    loadConsultas();
  }, [loadConsultas]);

  useEffect(() => {
    const targetConsultaId = location.state?.targetConsultaId;
    if (!targetConsultaId || consultas.length === 0) return;
    const scrollAndHighlight = () => {
      const element = document.getElementById(`consulta-${targetConsultaId}`);
      if (!element) return;
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const prevBg = element.style.backgroundColor;
      element.style.backgroundColor = '#fff3cd';
      setTimeout(() => {
        element.style.backgroundColor = prevBg;
      }, 1800);
      window.history.replaceState({}, document.title);
    };
    const t = setTimeout(scrollAndHighlight, 120);
    return () => clearTimeout(t);
  }, [location.state, consultas]);

  const cleanNamePart = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).replace(/^\s*0+/, '').trim();
  };

  const getClienteNombre = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    if (!cliente) return 'Cliente no encontrado';
    const nombre = cleanNamePart(cliente.nombre);
    const apellidos = cleanNamePart(cliente.apellidos);
    return `${nombre} ${apellidos}`.trim();
  };

  const getParticipantesNombres = (consulta) => {
    if (!consulta.es_grupal || !consulta.participantes || consulta.participantes.length === 0) {
      return getClienteNombre(consulta.cliente_id);
    }
    return consulta.participantes
      .map(p => `${cleanNamePart(p.nombre)} ${cleanNamePart(p.apellidos)}`.trim())
      .join(', ');
  };

  const handleCreate = () => {
    setEditingConsulta(null);
    setShowForm(true);
  };

  const handleEdit = (consulta) => {
    setEditingConsulta(consulta);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar esta ${labels.sesion_lower}?`)) {
      try {
        await consultasAPI.delete(id);
        setSuccess(`${labels.sesion} eliminada correctamente`);
        loadConsultas();
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError(`Error al eliminar la ${labels.sesion_lower}`);
        console.error(err);
      }
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingConsulta(null);
  };

  const handleFormSubmit = async (data) => {
    try {
      if (editingConsulta) {
        await consultasAPI.update(editingConsulta.id, data);
        setSuccess(`${labels.sesion} actualizada correctamente`);
      } else {
        await consultasAPI.create(data);
        setSuccess(`${labels.sesion} creada correctamente`);
      }
      setShowForm(false);
      setEditingConsulta(null);
      loadConsultas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Error al guardar la ${labels.sesion_lower}`);
      console.error(err);
    }
  };

  const handleStartEditField = (consultaId, fieldName, currentValue) => {
    setEditingFields(prev => ({
      ...prev,
      [`${consultaId}_${fieldName}`]: currentValue || ''
    }));
  };

  const handleCancelEditField = (consultaId, fieldName) => {
    setEditingFields(prev => {
      const newState = { ...prev };
      delete newState[`${consultaId}_${fieldName}`];
      return newState;
    });
  };

  const handleFieldChange = (consultaId, fieldName, value) => {
    setEditingFields(prev => ({
      ...prev,
      [`${consultaId}_${fieldName}`]: value
    }));
  };

  const handleSaveField = async (consultaId, fieldName) => {
    const fieldKey = `${consultaId}_${fieldName}`;
    const newValue = editingFields[fieldKey];
    
    if (newValue === undefined) return;

    setSavingFields(prev => ({ ...prev, [fieldKey]: true }));

    try {
      // Obtener la consulta actual para mantener los demás campos
      const consulta = consultas.find(c => c.id === consultaId);
      if (!consulta) {
        throw new Error('Consulta no encontrada');
      }

      // Preparar los datos para actualizar
      const updateData = {
        ...consulta,
        [fieldName]: newValue,
        cliente_id: consulta.cliente_id
      };

      await consultasAPI.update(consultaId, updateData);
      
      // Limpiar el estado de edición
      setEditingFields(prev => {
        const newState = { ...prev };
        delete newState[fieldKey];
        return newState;
      });

      setSuccess(`${fieldName === 'objetivos' ? 'Objetivo' : 
                   fieldName === 'contexto' ? 'Contexto' :
                   fieldName === 'contexto_sesion_1' ? 'Contexto Sesión 1' :
                   fieldName === 'observaciones_cliente' ? 'Observaciones Cliente' :
                   'Observaciones Coach'} actualizado correctamente`);
      
      // Recargar las consultas
      loadConsultas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Error al guardar ${fieldName}`);
      console.error(err);
    } finally {
      setSavingFields(prev => {
        const newState = { ...prev };
        delete newState[fieldKey];
        return newState;
      });
    }
  };

  // Función para filtrar consultas por fecha
  const getConsultasFiltradas = () => {
    let filtradas = consultas;
    
    // Filtro por fecha inicio
    if (fechaInicio) {
      filtradas = filtradas.filter(consulta => {
        const fechaConsulta = consulta.fecha_consulta ? consulta.fecha_consulta.split('T')[0] : null;
        return fechaConsulta && fechaConsulta >= fechaInicio;
      });
    }
    
    // Filtro por fecha fin
    if (fechaFin) {
      filtradas = filtradas.filter(consulta => {
        const fechaConsulta = consulta.fecha_consulta ? consulta.fecha_consulta.split('T')[0] : null;
        return fechaConsulta && fechaConsulta <= fechaFin;
      });
    }
    
    return filtradas;
  };

  if (loading) {
    return <div className="loading">Cargando {labels.sesiones_lower}...</div>;
  }

  const consultasFiltradas = getConsultasFiltradas();

  return (
    <div className="consultas">
      <div className="page-header">
        <h2>Gestión de {labels.sesiones}</h2>
        <button className="btn btn-primary" onClick={handleCreate}>
          {labels.nueva_sesion}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="filtros">
        <div className="filtros-grid">
          <div className="form-group">
            <label>Filtrar por Cliente:</label>
            <select
              value={filtroCliente}
              onChange={(e) => setFiltroCliente(e.target.value)}
            >
              <option value="">Todos los clientes</option>
              {clientes.map(cliente => (
                <option key={cliente.id} value={cliente.id}>
                  {cliente.nombre} {cliente.apellidos}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Fecha Inicio:</label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Fecha Fin:</label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="consultas-list">
        {consultasFiltradas.map(consulta => (
          <div key={consulta.id} id={`consulta-${consulta.id}`} className="consulta-card">
            <div className="consulta-header">
              <div>
                <h3>
                  {Boolean(consulta.es_grupal) && <span style={{ fontSize: '12px', background: '#e3f2fd', color: '#1565c0', padding: '1px 6px', borderRadius: '4px', marginRight: '8px', verticalAlign: 'middle' }}>Grupal</span>}
                  {getParticipantesNombres(consulta)}
                </h3>
                <p className="consulta-fecha">
                  {new Date(consulta.fecha_consulta).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                  {consulta.numero_sesion && (
                    <span style={{ marginLeft: '10px', fontWeight: 600, color: '#007bff' }}>
                      - {labels.sesion} #{consulta.numero_sesion}
                    </span>
                  )}
                </p>
              </div>
              <div className="consulta-actions">
                {facturacionHabilitada && (
                  <button
                    className="btn btn-sm"
                    style={{ background: '#17a2b8', color: '#fff', border: 'none' }}
                    onClick={() => {
                      const clienteNom = getClienteNombre(consulta.cliente_id);
                      const fecha = consulta.fecha_consulta ? new Date(consulta.fecha_consulta).toLocaleDateString('es-ES') : '';
                      navigate('/facturacion', {
                        state: {
                          crearFactura: true,
                          cliente_id: consulta.cliente_id,
                          consulta_id: consulta.id,
                          descripcion_linea: `${labels.sesion} #${consulta.numero_sesion} - ${clienteNom} - ${fecha}`,
                        }
                      });
                    }}
                    title="Generar factura para esta sesión"
                  >
                    Generar Factura
                  </button>
                )}
                <button className="btn btn-edit btn-sm" onClick={() => handleEdit(consulta)}>
                  Editar
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(consulta.id)}>
                  Eliminar
                </button>
              </div>
            </div>
            <div className="consulta-content">
              {/* Objetivos */}
              {(consulta.objetivos || editingFields[`${consulta.id}_objetivos`] !== undefined || true) && (
                <div className="consulta-section">
                  <div className="consulta-section-header">
                    <h4>Objetivo de la {labels.sesion_lower}:</h4>
                    {editingFields[`${consulta.id}_objetivos`] === undefined ? (
                      <button
                        className="btn btn-xs btn-edit"
                        onClick={() => handleStartEditField(consulta.id, 'objetivos', consulta.objetivos)}
                      >
                        ✏️ Editar
                      </button>
                    ) : (
                      <div className="consulta-edit-actions">
                        <button
                          className="btn btn-xs btn-primary"
                          onClick={() => handleSaveField(consulta.id, 'objetivos')}
                          disabled={savingFields[`${consulta.id}_objetivos`]}
                        >
                          {savingFields[`${consulta.id}_objetivos`] ? 'Guardando...' : '💾 Guardar'}
                        </button>
                        <button
                          className="btn btn-xs btn-secondary"
                          onClick={() => handleCancelEditField(consulta.id, 'objetivos')}
                          disabled={savingFields[`${consulta.id}_objetivos`]}
                        >
                          ✖️ Cancelar
                        </button>
                      </div>
                    )}
                  </div>
                  {editingFields[`${consulta.id}_objetivos`] !== undefined ? (
                    <Editor
                      name="objetivos"
                      value={editingFields[`${consulta.id}_objetivos`]}
                      onChange={(e) => handleFieldChange(consulta.id, 'objetivos', e.target.value)}
                      height={200}
                    />
                  ) : consulta.objetivos ? (
                    <div dangerouslySetInnerHTML={safeHtml(consulta.objetivos)} />
                  ) : (
                    <div className="consulta-empty">No hay objetivo registrado. Haz clic en "Editar" para agregar.</div>
                  )}
                </div>
              )}

              {/* Contexto Sesión 1 */}
              {(consulta.contexto_sesion_1 || editingFields[`${consulta.id}_contexto_sesion_1`] !== undefined) && consulta.numero_sesion === 1 && (
                <div className="consulta-section">
                  <div className="consulta-section-header">
                    <h4>Contexto {labels.sesion} 1:</h4>
                    {editingFields[`${consulta.id}_contexto_sesion_1`] === undefined ? (
                      <button
                        className="btn btn-xs btn-edit"
                        onClick={() => handleStartEditField(consulta.id, 'contexto_sesion_1', consulta.contexto_sesion_1)}
                      >
                        ✏️ Editar
                      </button>
                    ) : (
                      <div className="consulta-edit-actions">
                        <button
                          className="btn btn-xs btn-primary"
                          onClick={() => handleSaveField(consulta.id, 'contexto_sesion_1')}
                          disabled={savingFields[`${consulta.id}_contexto_sesion_1`]}
                        >
                          {savingFields[`${consulta.id}_contexto_sesion_1`] ? 'Guardando...' : '💾 Guardar'}
                        </button>
                        <button
                          className="btn btn-xs btn-secondary"
                          onClick={() => handleCancelEditField(consulta.id, 'contexto_sesion_1')}
                          disabled={savingFields[`${consulta.id}_contexto_sesion_1`]}
                        >
                          ✖️ Cancelar
                        </button>
                      </div>
                    )}
                  </div>
                  {editingFields[`${consulta.id}_contexto_sesion_1`] !== undefined ? (
                    <Editor
                      name="contexto_sesion_1"
                      value={editingFields[`${consulta.id}_contexto_sesion_1`]}
                      onChange={(e) => handleFieldChange(consulta.id, 'contexto_sesion_1', e.target.value)}
                      height={300}
                    />
                  ) : (
                    <div dangerouslySetInnerHTML={safeHtml(consulta.contexto_sesion_1)} />
                  )}
                </div>
              )}

              {/* Contexto */}
              {(consulta.contexto || editingFields[`${consulta.id}_contexto`] !== undefined || true) && (
                <div className="consulta-section">
                  <div className="consulta-section-header">
                    <h4>Contexto:</h4>
                    {editingFields[`${consulta.id}_contexto`] === undefined ? (
                      <button
                        className="btn btn-xs btn-edit"
                        onClick={() => handleStartEditField(consulta.id, 'contexto', consulta.contexto)}
                      >
                        ✏️ Editar
                      </button>
                    ) : (
                      <div className="consulta-edit-actions">
                        <button
                          className="btn btn-xs btn-primary"
                          onClick={() => handleSaveField(consulta.id, 'contexto')}
                          disabled={savingFields[`${consulta.id}_contexto`]}
                        >
                          {savingFields[`${consulta.id}_contexto`] ? 'Guardando...' : '💾 Guardar'}
                        </button>
                        <button
                          className="btn btn-xs btn-secondary"
                          onClick={() => handleCancelEditField(consulta.id, 'contexto')}
                          disabled={savingFields[`${consulta.id}_contexto`]}
                        >
                          ✖️ Cancelar
                        </button>
                      </div>
                    )}
                  </div>
                  {editingFields[`${consulta.id}_contexto`] !== undefined ? (
                    <Editor
                      name="contexto"
                      value={editingFields[`${consulta.id}_contexto`]}
                      onChange={(e) => handleFieldChange(consulta.id, 'contexto', e.target.value)}
                      height={300}
                    />
                  ) : consulta.contexto ? (
                    <div dangerouslySetInnerHTML={safeHtml(consulta.contexto)} />
                  ) : (
                    <div className="consulta-empty">No hay contexto registrado. Haz clic en "Editar" para agregar.</div>
                  )}
                </div>
              )}

              {/* Observaciones Cliente */}
              {(consulta.observaciones_cliente || editingFields[`${consulta.id}_observaciones_cliente`] !== undefined || true) && (
                <div className="consulta-section">
                  <div className="consulta-section-header">
                    <h4>Observaciones Cliente:</h4>
                    {editingFields[`${consulta.id}_observaciones_cliente`] === undefined ? (
                      <button
                        className="btn btn-xs btn-edit"
                        onClick={() => handleStartEditField(consulta.id, 'observaciones_cliente', consulta.observaciones_cliente)}
                      >
                        ✏️ Editar
                      </button>
                    ) : (
                      <div className="consulta-edit-actions">
                        <button
                          className="btn btn-xs btn-primary"
                          onClick={() => handleSaveField(consulta.id, 'observaciones_cliente')}
                          disabled={savingFields[`${consulta.id}_observaciones_cliente`]}
                        >
                          {savingFields[`${consulta.id}_observaciones_cliente`] ? 'Guardando...' : '💾 Guardar'}
                        </button>
                        <button
                          className="btn btn-xs btn-secondary"
                          onClick={() => handleCancelEditField(consulta.id, 'observaciones_cliente')}
                          disabled={savingFields[`${consulta.id}_observaciones_cliente`]}
                        >
                          ✖️ Cancelar
                        </button>
                      </div>
                    )}
                  </div>
                  {editingFields[`${consulta.id}_observaciones_cliente`] !== undefined ? (
                    <Editor
                      name="observaciones_cliente"
                      value={editingFields[`${consulta.id}_observaciones_cliente`]}
                      onChange={(e) => handleFieldChange(consulta.id, 'observaciones_cliente', e.target.value)}
                      height={200}
                    />
                  ) : consulta.observaciones_cliente ? (
                    <div dangerouslySetInnerHTML={safeHtml(consulta.observaciones_cliente)} />
                  ) : (
                    <div className="consulta-empty">No hay observaciones del cliente registradas. Haz clic en "Editar" para agregar.</div>
                  )}
                </div>
              )}

              {/* Observaciones Coach */}
              {(consulta.observaciones_coach || editingFields[`${consulta.id}_observaciones_coach`] !== undefined || true) && (
                <div className="consulta-section">
                  <div className="consulta-section-header">
                    <h4>Observaciones Coach:</h4>
                    {editingFields[`${consulta.id}_observaciones_coach`] === undefined ? (
                      <button
                        className="btn btn-xs btn-edit"
                        onClick={() => handleStartEditField(consulta.id, 'observaciones_coach', consulta.observaciones_coach)}
                      >
                        ✏️ Editar
                      </button>
                    ) : (
                      <div className="consulta-edit-actions">
                        <button
                          className="btn btn-xs btn-primary"
                          onClick={() => handleSaveField(consulta.id, 'observaciones_coach')}
                          disabled={savingFields[`${consulta.id}_observaciones_coach`]}
                        >
                          {savingFields[`${consulta.id}_observaciones_coach`] ? 'Guardando...' : '💾 Guardar'}
                        </button>
                        <button
                          className="btn btn-xs btn-secondary"
                          onClick={() => handleCancelEditField(consulta.id, 'observaciones_coach')}
                          disabled={savingFields[`${consulta.id}_observaciones_coach`]}
                        >
                          ✖️ Cancelar
                        </button>
                      </div>
                    )}
                  </div>
                  {editingFields[`${consulta.id}_observaciones_coach`] !== undefined ? (
                    <Editor
                      name="observaciones_coach"
                      value={editingFields[`${consulta.id}_observaciones_coach`]}
                      onChange={(e) => handleFieldChange(consulta.id, 'observaciones_coach', e.target.value)}
                      height={200}
                    />
                  ) : consulta.observaciones_coach ? (
                    <div dangerouslySetInnerHTML={safeHtml(consulta.observaciones_coach)} />
                  ) : (
                    <div className="consulta-empty">No hay observaciones del coach registradas. Haz clic en "Editar" para agregar.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {consultas.length === 0 && (
        <div className="empty-state">
          <p>No hay {labels.sesiones_lower} registradas. Crea la primera haciendo clic en "{labels.nueva_sesion}".</p>
        </div>
      )}
      {consultas.length > 0 && consultasFiltradas.length === 0 && (
        <div className="empty-state">
          <p>No hay sesiones que coincidan con los filtros seleccionados.</p>
        </div>
      )}

      {showForm && createPortal(
        <ConsultaForm
          consulta={editingConsulta}
          clientes={clientes}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />,
        document.body
      )}
    </div>
  );
}

export default Consultas;

